/**
 * Mock data for Journey Mapper.
 * Used as a TEMPLATE for new users (ideas as inspiration, empty touchpoints)
 * and as fallback when Supabase is not configured.
 *
 * All content is in Czech.
 */

export type JourneyType = 'customer' | 'employee' | 'personal';

export interface MockTouchpoint {
  id: string;
  title: string;
  type: 'digital' | 'physical';
  canInfluence: boolean;
  isCustom?: boolean;
}

export interface MockIdea {
  id: string;
  title: string;
  source: 'global' | 'user';
  assignedToPhase?: number;
  assignedToTouchpointId?: string;
}

export interface MockPhase {
  id: string;
  order: number;
  name: string;
  color: string;
  headerBg: string;
  digitalTouchpoints: MockTouchpoint[];
  physicalTouchpoints: MockTouchpoint[];
  ideas: MockIdea[];
}

export interface MockJourney {
  type: JourneyType;
  label: string;
  description: string;
  phases: MockPhase[];
}

// Sdílené barvy pro 5 fází (1 = červená/start, 5 = modrá/finále)
const PHASE_COLORS: Array<{ color: string; headerBg: string }> = [
  { color: 'border-red-400',    headerBg: 'bg-red-100' },
  { color: 'border-orange-400', headerBg: 'bg-orange-100' },
  { color: 'border-green-400',  headerBg: 'bg-green-100' },
  { color: 'border-teal-400',   headerBg: 'bg-teal-100' },
  { color: 'border-blue-400',   headerBg: 'bg-blue-100' },
];

// ─── Customer Journey ───────────────────────────────────────────────────────
// Povědomí → Zvažování → Nákup → Servis → Věrnost

const customerPhases: MockPhase[] = [
  {
    id: 'cz-customer-phase-01', order: 1, name: 'Povědomí',
    ...PHASE_COLORS[0],
    digitalTouchpoints: [
      { id: 'c-d-1', title: 'Reklama na sociálních sítích', type: 'digital', canInfluence: true },
      { id: 'c-d-2', title: 'SEO & Google Ads', type: 'digital', canInfluence: true },
      { id: 'c-d-3', title: 'Newsletter kampaň', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'c-p-1', title: 'Billboardy a plakáty', type: 'physical', canInfluence: true },
      { id: 'c-p-2', title: 'Veletrhy a konference', type: 'physical', canInfluence: false },
      { id: 'c-p-3', title: 'Tiskové materiály', type: 'physical', canInfluence: true },
    ],
    ideas: [
      { id: 'c-i-1', title: 'Spolupráce s influencery', source: 'global' },
      { id: 'c-i-2', title: 'Podcastová reklama', source: 'global' },
      { id: 'c-i-3', title: 'Video tutoriály na YouTube', source: 'global' },
    ],
  },
  {
    id: 'cz-customer-phase-02', order: 2, name: 'Zvažování',
    ...PHASE_COLORS[1],
    digitalTouchpoints: [
      { id: 'c-d-4', title: 'Webová stránka s produkty', type: 'digital', canInfluence: true },
      { id: 'c-d-5', title: 'Recenze na Heurece', type: 'digital', canInfluence: false },
      { id: 'c-d-6', title: 'Online webinář a demo', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'c-p-4', title: 'Návštěva kamenné prodejny', type: 'physical', canInfluence: true },
      { id: 'c-p-5', title: 'Vzorky produktů zdarma', type: 'physical', canInfluence: true },
      { id: 'c-p-6', title: 'Obchodní schůzka', type: 'physical', canInfluence: true },
    ],
    ideas: [
      { id: 'c-i-4', title: '30denní zkušební verze', source: 'global' },
      { id: 'c-i-5', title: 'Srovnávací tabulka s konkurencí', source: 'global' },
      { id: 'c-i-6', title: 'Reference a případové studie', source: 'global' },
    ],
  },
  {
    id: 'cz-customer-phase-03', order: 3, name: 'Nákup',
    ...PHASE_COLORS[2],
    digitalTouchpoints: [
      { id: 'c-d-7', title: 'Online pokladna / e-shop', type: 'digital', canInfluence: true },
      { id: 'c-d-8', title: 'Live chat podpora', type: 'digital', canInfluence: true },
      { id: 'c-d-9', title: 'Potvrzovací e-mail objednávky', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'c-p-7', title: 'Nákup v prodejně', type: 'physical', canInfluence: true },
      { id: 'c-p-8', title: 'Podpis smlouvy', type: 'physical', canInfluence: true },
      { id: 'c-p-9', title: 'Platební terminál', type: 'physical', canInfluence: false },
    ],
    ideas: [
      { id: 'c-i-7', title: 'Nákup jedním kliknutím', source: 'global' },
      { id: 'c-i-8', title: 'Splátkový kalendář', source: 'global' },
      { id: 'c-i-9', title: 'Zvýhodněné balíčky', source: 'global' },
    ],
  },
  {
    id: 'cz-customer-phase-04', order: 4, name: 'Servis',
    ...PHASE_COLORS[3],
    digitalTouchpoints: [
      { id: 'c-d-10', title: 'Onboardingová e-mailová série', type: 'digital', canInfluence: true },
      { id: 'c-d-11', title: 'Věrnostní mobilní aplikace', type: 'digital', canInfluence: true },
      { id: 'c-d-12', title: 'Zákaznický portál', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'c-p-10', title: 'Zákaznická podpora na lince', type: 'physical', canInfluence: true },
      { id: 'c-p-11', title: 'Balení a unboxing zážitek', type: 'physical', canInfluence: true },
      { id: 'c-p-12', title: 'Zpětná vazba po nákupu', type: 'physical', canInfluence: true },
    ],
    ideas: [
      { id: 'c-i-10', title: 'Self-service znalostní báze', source: 'global' },
      { id: 'c-i-11', title: 'Personalizované doporučení', source: 'global' },
      { id: 'c-i-12', title: 'Komunitní fórum zákazníků', source: 'global' },
    ],
  },
  {
    id: 'cz-customer-phase-05', order: 5, name: 'Věrnost',
    ...PHASE_COLORS[4],
    digitalTouchpoints: [
      { id: 'c-d-13', title: 'Referenční program', type: 'digital', canInfluence: true },
      { id: 'c-d-14', title: 'Sdílení na sociálních sítích', type: 'digital', canInfluence: false },
      { id: 'c-d-15', title: 'Žádost o recenzi', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'c-p-13', title: 'VIP akce pro věrné zákazníky', type: 'physical', canInfluence: true },
      { id: 'c-p-14', title: 'Ambasadorský program', type: 'physical', canInfluence: true },
      { id: 'c-p-15', title: 'Dárkové poděkování', type: 'physical', canInfluence: true },
    ],
    ideas: [
      { id: 'c-i-13', title: 'Videa s referencemi zákazníků', source: 'global' },
      { id: 'c-i-14', title: 'Affiliate program', source: 'global' },
      { id: 'c-i-15', title: 'Spoluúčast na vývoji produktu', source: 'global' },
    ],
  },
];

// ─── Employee Journey ────────────────────────────────────────────────────────
// Nábor → Zvažování → Nástup → Rozvoj → Angažovanost

const employeePhases: MockPhase[] = [
  {
    id: 'cz-employee-phase-01', order: 1, name: 'Nábor',
    ...PHASE_COLORS[0],
    digitalTouchpoints: [
      { id: 'e-d-1', title: 'Inzerát na pracovních portálech', type: 'digital', canInfluence: true },
      { id: 'e-d-2', title: 'LinkedIn profil firmy', type: 'digital', canInfluence: true },
      { id: 'e-d-3', title: 'Online přihláška', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'e-p-1', title: 'Kariérní veletrh', type: 'physical', canInfluence: false },
      { id: 'e-p-2', title: 'Doporučení od zaměstnanců', type: 'physical', canInfluence: true },
      { id: 'e-p-3', title: 'Tištěný inzerát', type: 'physical', canInfluence: true },
    ],
    ideas: [
      { id: 'e-i-1', title: 'Video o firemní kultuře', source: 'global' },
      { id: 'e-i-2', title: 'Gamifikovaný assessment', source: 'global' },
      { id: 'e-i-3', title: 'Virtuální prohlídka kanceláří', source: 'global' },
    ],
  },
  {
    id: 'cz-employee-phase-02', order: 2, name: 'Zvažování',
    ...PHASE_COLORS[1],
    digitalTouchpoints: [
      { id: 'e-d-4', title: 'Webové kariérní stránky', type: 'digital', canInfluence: true },
      { id: 'e-d-5', title: 'Recenze na Atmoskopu', type: 'digital', canInfluence: false },
      { id: 'e-d-6', title: 'LinkedIn profily zaměstnanců', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'e-p-4', title: 'Pohovor s HR', type: 'physical', canInfluence: true },
      { id: 'e-p-5', title: 'Setkání s budoucím týmem', type: 'physical', canInfluence: true },
      { id: 'e-p-6', title: 'Prohlídka kanceláří', type: 'physical', canInfluence: true },
    ],
    ideas: [
      { id: 'e-i-4', title: 'Transparentní platové rozpětí', source: 'global' },
      { id: 'e-i-5', title: 'Realistický náhled na práci', source: 'global' },
      { id: 'e-i-6', title: 'Reference od bývalých zaměstnanců', source: 'global' },
    ],
  },
  {
    id: 'cz-employee-phase-03', order: 3, name: 'Nástup',
    ...PHASE_COLORS[2],
    digitalTouchpoints: [
      { id: 'e-d-7', title: 'Uvítací e-mail a portál', type: 'digital', canInfluence: true },
      { id: 'e-d-8', title: 'Online školení a e-learning', type: 'digital', canInfluence: true },
      { id: 'e-d-9', title: 'Digitální podpis smlouvy', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'e-p-7', title: 'Uvítací balíček nováčka', type: 'physical', canInfluence: true },
      { id: 'e-p-8', title: 'Osobní průvodce / buddy', type: 'physical', canInfluence: true },
      { id: 'e-p-9', title: 'Prohlídka firmy', type: 'physical', canInfluence: true },
    ],
    ideas: [
      { id: 'e-i-7', title: '90denní onboardingový plán', source: 'global' },
      { id: 'e-i-8', title: 'Mentorský program', source: 'global' },
      { id: 'e-i-9', title: 'Gamifikovaný onboarding', source: 'global' },
    ],
  },
  {
    id: 'cz-employee-phase-04', order: 4, name: 'Rozvoj',
    ...PHASE_COLORS[3],
    digitalTouchpoints: [
      { id: 'e-d-10', title: 'Platforma pro vzdělávání', type: 'digital', canInfluence: true },
      { id: 'e-d-11', title: 'Systém hodnocení výkonu', type: 'digital', canInfluence: true },
      { id: 'e-d-12', title: 'Interní wiki a znalostní báze', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'e-p-10', title: 'Workshopy a semináře', type: 'physical', canInfluence: true },
      { id: 'e-p-11', title: 'Hodnotící pohovor', type: 'physical', canInfluence: true },
      { id: 'e-p-12', title: 'Koučovací sezení', type: 'physical', canInfluence: true },
    ],
    ideas: [
      { id: 'e-i-10', title: 'Vzdělávací rozpočet pro zaměstnance', source: 'global' },
      { id: 'e-i-11', title: 'Peer-to-peer sdílení znalostí', source: 'global' },
      { id: 'e-i-12', title: 'Kariérní mapa a plán růstu', source: 'global' },
    ],
  },
  {
    id: 'cz-employee-phase-05', order: 5, name: 'Angažovanost',
    ...PHASE_COLORS[4],
    digitalTouchpoints: [
      { id: 'e-d-13', title: 'Průzkum spokojenosti zaměstnanců', type: 'digital', canInfluence: true },
      { id: 'e-d-14', title: 'Intranet a komunikační platforma', type: 'digital', canInfluence: true },
      { id: 'e-d-15', title: 'Program odměn a uznání', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'e-p-13', title: 'Firemní akce a teambuilding', type: 'physical', canInfluence: true },
      { id: 'e-p-14', title: 'Benefitní program', type: 'physical', canInfluence: true },
      { id: 'e-p-15', title: 'Pravidelné 1:1 schůzky', type: 'physical', canInfluence: true },
    ],
    ideas: [
      { id: 'e-i-13', title: 'Pulzní průzkumy každý měsíc', source: 'global' },
      { id: 'e-i-14', title: 'Iniciativy řízené zaměstnanci', source: 'global' },
      { id: 'e-i-15', title: 'Flexibilní pracovní benefity', source: 'global' },
    ],
  },
];

// ─── Personal Journey ────────────────────────────────────────────────────────
// Objev → Poznávání → Závazek → Péče → Věrnost

const personalPhases: MockPhase[] = [
  {
    id: 'cz-personal-phase-01', order: 1, name: 'Objev',
    ...PHASE_COLORS[0],
    digitalTouchpoints: [
      { id: 'p-d-1', title: 'Seznamovací aplikace', type: 'digital', canInfluence: true },
      { id: 'p-d-2', title: 'Sociální sítě', type: 'digital', canInfluence: true },
      { id: 'p-d-3', title: 'Online komunity a fóra', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'p-p-1', title: 'Setkání přes přátele', type: 'physical', canInfluence: true },
      { id: 'p-p-2', title: 'Společenské akce a večírky', type: 'physical', canInfluence: true },
      { id: 'p-p-3', title: 'Hobby kroužky a sport', type: 'physical', canInfluence: true },
    ],
    ideas: [
      { id: 'p-i-1', title: 'Chodit na nová místa', source: 'global' },
      { id: 'p-i-2', title: 'Cestování solo', source: 'global' },
      { id: 'p-i-3', title: 'Speed dating večery', source: 'global' },
    ],
  },
  {
    id: 'cz-personal-phase-02', order: 2, name: 'Poznávání',
    ...PHASE_COLORS[1],
    digitalTouchpoints: [
      { id: 'p-d-4', title: 'Chatování a video hovory', type: 'digital', canInfluence: true },
      { id: 'p-d-5', title: 'Sdílení obsahu (memy, hudba)', type: 'digital', canInfluence: true },
      { id: 'p-d-6', title: 'Sdílené playlisty', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'p-p-4', title: 'První rande', type: 'physical', canInfluence: true },
      { id: 'p-p-5', title: 'Společné výlety', type: 'physical', canInfluence: true },
      { id: 'p-p-6', title: 'Setkání s přáteli druhého', type: 'physical', canInfluence: true },
    ],
    ideas: [
      { id: 'p-i-4', title: 'Otevřené hluboké rozhovory', source: 'global' },
      { id: 'p-i-5', title: 'Sdílení hodnot a snů', source: 'global' },
      { id: 'p-i-6', title: 'Společný zážitek mimo komfort', source: 'global' },
    ],
  },
  {
    id: 'cz-personal-phase-03', order: 3, name: 'Závazek',
    ...PHASE_COLORS[2],
    digitalTouchpoints: [
      { id: 'p-d-7', title: 'Sdílený kalendář', type: 'digital', canInfluence: true },
      { id: 'p-d-8', title: 'Společný spořicí účet', type: 'digital', canInfluence: true },
      { id: 'p-d-9', title: 'Oficiální status na sítích', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'p-p-7', title: 'Stěhování spolu', type: 'physical', canInfluence: true },
      { id: 'p-p-8', title: 'Zásnuby a svatba', type: 'physical', canInfluence: true },
      { id: 'p-p-9', title: 'Seznámení s rodinou', type: 'physical', canInfluence: true },
    ],
    ideas: [
      { id: 'p-i-7', title: 'Předmanželská smlouva o očekáváních', source: 'global' },
      { id: 'p-i-8', title: 'Společný plán na 5 let', source: 'global' },
      { id: 'p-i-9', title: 'Páry koučink před svatbou', source: 'global' },
    ],
  },
  {
    id: 'cz-personal-phase-04', order: 4, name: 'Péče',
    ...PHASE_COLORS[3],
    digitalTouchpoints: [
      { id: 'p-d-10', title: 'Aplikace na sdílení nákupů a úkolů', type: 'digital', canInfluence: true },
      { id: 'p-d-11', title: 'Vzpomínkové foto alba', type: 'digital', canInfluence: true },
      { id: 'p-d-12', title: 'Připomínky důležitých dat', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'p-p-10', title: 'Pravidelná rande night', type: 'physical', canInfluence: true },
      { id: 'p-p-11', title: 'Společné rituály (snídaně, sport)', type: 'physical', canInfluence: true },
      { id: 'p-p-12', title: 'Pozornosti a dárky', type: 'physical', canInfluence: true },
    ],
    ideas: [
      { id: 'p-i-10', title: 'Týdenní check-in o vztahu', source: 'global' },
      { id: 'p-i-11', title: 'Nové společné koníčky', source: 'global' },
      { id: 'p-i-12', title: 'Páry terapie preventivně', source: 'global' },
    ],
  },
  {
    id: 'cz-personal-phase-05', order: 5, name: 'Věrnost',
    ...PHASE_COLORS[4],
    digitalTouchpoints: [
      { id: 'p-d-13', title: 'Sdílení vztahu na sítích', type: 'digital', canInfluence: true },
      { id: 'p-d-14', title: 'Dlouhodobé společné projekty', type: 'digital', canInfluence: true },
      { id: 'p-d-15', title: 'Společné finanční plánování', type: 'digital', canInfluence: true },
    ],
    physicalTouchpoints: [
      { id: 'p-p-13', title: 'Výročí a oslavy', type: 'physical', canInfluence: true },
      { id: 'p-p-14', title: 'Společné cesty životem', type: 'physical', canInfluence: true },
      { id: 'p-p-15', title: 'Vzájemná podpora v krizích', type: 'physical', canInfluence: true },
    ],
    ideas: [
      { id: 'p-i-13', title: 'Obnova slibů po letech', source: 'global' },
      { id: 'p-i-14', title: 'Společné sebevzdělávání ve vztazích', source: 'global' },
      { id: 'p-i-15', title: 'Mentoring pro mladší páry', source: 'global' },
    ],
  },
];

// ─── Journey Map registry ────────────────────────────────────────────────────

export const MOCK_JOURNEYS: Record<JourneyType, MockJourney> = {
  customer: {
    type: 'customer',
    label: 'Zákazník',
    description: 'Mapujte cestu zákazníka od prvního kontaktu až po loajalitu a doporučení',
    phases: customerPhases,
  },
  employee: {
    type: 'employee',
    label: 'Zaměstnanec',
    description: 'Sledujte zkušenost zaměstnance od náboru po angažovanost',
    phases: employeePhases,
  },
  personal: {
    type: 'personal',
    label: 'Osobní cesta',
    description: 'Vytvořte vlastní mapu vztahu od prvního setkání po dlouholetou věrnost',
    phases: personalPhases,
  },
};

// ─── Helper: vytvoř prázdný stav pro nového uživatele ───────────────────────
// Phases mají jen názvy + barvy + ideas (jako inspirace), ŽÁDNÉ touchpointy.
// Uživatel si je vyplní sám přetažením nápadů nebo přidáním vlastních.

export function createEmptyJourneyState(type: JourneyType): MockPhase[] {
  return MOCK_JOURNEYS[type].phases.map(phase => ({
    id: phase.id,
    order: phase.order,
    name: phase.name,
    color: phase.color,
    headerBg: phase.headerBg,
    digitalTouchpoints: [],
    physicalTouchpoints: [],
    // Zkopíruj globální ideas jako inspiraci do "Nápady" sekce.
    // Uživatel je může přesunout na touchpointy nebo přidat vlastní.
    ideas: phase.ideas.map(i => ({ ...i })),
  }));
}

// ─── Global ideas (admin section) ───────────────────────────────────────────

export interface MockGlobalIdea {
  id: string;
  title: string;
  createdAt: string;
}

export const MOCK_GLOBAL_IDEAS: MockGlobalIdea[] = [
  { id: 'g-1', title: 'Personalizace zákaznického zážitku pomocí AI', createdAt: '2026-01-10' },
  { id: 'g-2', title: 'Omnichannel integrace touchpointů', createdAt: '2026-01-15' },
  { id: 'g-3', title: 'Proaktivní zákaznická podpora', createdAt: '2026-01-20' },
  { id: 'g-4', title: 'Gamifikace věrnostního programu', createdAt: '2026-02-01' },
  { id: 'g-5', title: 'Self-service zákaznický portál', createdAt: '2026-02-10' },
  { id: 'g-6', title: 'Automatizace onboarding procesu', createdAt: '2026-02-15' },
  { id: 'g-7', title: 'Data-driven personalizace komunikace', createdAt: '2026-03-01' },
  { id: 'g-8', title: 'Chatbot pro 24/7 podporu', createdAt: '2026-03-10' },
];

// ─── User suggestions ────────────────────────────────────────────────────────

export interface MockSuggestion {
  id: string;
  userId: string;
  userEmail: string;
  title: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export const MOCK_SUGGESTIONS: MockSuggestion[] = [
  { id: 's-1', userId: 'user-1', userEmail: 'jana.novakova@example.com', title: 'Přidat AR/VR touchpoint do digitálních kanálů', status: 'pending', createdAt: '2026-04-15' },
  { id: 's-2', userId: 'user-2', userEmail: 'petr.kral@example.com', title: 'Integrovat zpětnou vazbu v reálném čase', status: 'pending', createdAt: '2026-04-20' },
  { id: 's-3', userId: 'user-3', userEmail: 'marie.horakova@example.com', title: 'Přidat touchpoint pro zákaznické komunity', status: 'approved', createdAt: '2026-04-22' },
  { id: 's-4', userId: 'user-4', userEmail: 'tomas.vlcek@example.com', title: 'Zahrnout doručovací zážitek jako touchpoint', status: 'rejected', createdAt: '2026-04-25' },
  { id: 's-5', userId: 'user-5', userEmail: 'lucie.srbova@example.com', title: 'Rozšířit sekci fyzických touchpointů o pop-up stánky', status: 'pending', createdAt: '2026-05-01' },
];
